import DOMPurify from 'dompurify';
import { escapeHtml } from '@dts/core';

export interface RenderedEmail {
  srcdoc: string;
  blockedImages: number;
}

/**
 * Turns untrusted email HTML into a self-contained document for a sandboxed iframe (no scripts, no forms).
 * Remote images are blocked unless allowed (tracking pixels), enforced both by rewriting and by a CSP meta tag.
 */
export function renderEmail(html: string | undefined, text: string | undefined, opts: { allowRemote: boolean; dark: boolean; inline: Record<string, string> }): RenderedEmail {
  let blocked = 0;
  const body = html?.trim()
    ? html
    : `<div style="white-space:pre-wrap;font-family:inherit">${linkify(escapeHtml(text ?? ''))}</div>`;

  const purify = DOMPurify();
  purify.addHook('afterSanitizeAttributes', (node) => {
    const el = node as Element;
    if (el.tagName === 'A') {
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
    }
    for (const attr of ['src', 'background', 'poster']) {
      const v = el.getAttribute?.(attr);
      if (!v) continue;
      if (v.startsWith('cid:')) {
        const data = opts.inline[v.slice(4).replace(/^<|>$/g, '')];
        if (data) el.setAttribute(attr, data);
        else el.removeAttribute(attr);
      } else if (/^https?:/i.test(v) && !opts.allowRemote) {
        el.setAttribute(`data-blocked-${attr}`, v);
        el.removeAttribute(attr);
        blocked++;
      }
    }
    const style = el.getAttribute?.('style');
    if (style && /url\(/i.test(style) && !opts.allowRemote) {
      el.setAttribute('style', style.replace(/url\([^)]*\)/gi, 'none'));
      blocked++;
    }
  });
  const clean = purify.sanitize(body, {
    WHOLE_DOCUMENT: false,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'textarea', 'select', 'meta', 'link', 'base'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'formaction'],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
  });

  const csp = `default-src 'none'; style-src 'unsafe-inline'; font-src data: https:; img-src data: blob:${opts.allowRemote ? ' https: http:' : ''};`;
  const fg = opts.dark ? '#e6e6f0' : '#1b1b26';
  const link = opts.dark ? '#a5b4fc' : '#4f46e5';
  // Most HTML mail is designed for white; in dark mode plain-text-ish mail is themed, designed mail keeps a light card.
  const designed = !!html && /<(table|img|div[^>]+style=[^>]*background)/i.test(html);
  const bg = opts.dark && !designed ? 'transparent' : designed ? '#ffffff' : 'transparent';
  const color = designed ? '#1b1b26' : fg;

  const srcdoc = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}">
<base target="_blank"><style>
html,body{margin:0;padding:0;background:${bg};color:${color};}
body{font:14.5px/1.6 'Inter Variable',-apple-system,'Segoe UI',sans-serif;padding:${designed ? '16px' : '2px 0'};word-wrap:break-word;overflow-wrap:anywhere;${designed ? 'border-radius:14px;' : ''}}
a{color:${designed ? '#4f46e5' : link}}
img{max-width:100%;height:auto}
table{max-width:100%}
blockquote{margin:0 0 0 .6em;padding-left:.8em;border-left:2px solid ${opts.dark && !designed ? '#3a3a52' : '#d4d4e0'};color:${opts.dark && !designed ? '#9d9db5' : '#5d5d72'}}
pre{white-space:pre-wrap}
</style></head><body>${clean}</body></html>`;
  return { srcdoc, blockedImages: blocked };
}

function linkify(s: string): string {
  return s.replace(/\bhttps?:\/\/[^\s<>"')]+/g, (u) => `<a href="${u}">${u}</a>`);
}
